/**
 * Bounds ROBUSTO del cadJson: min/max de las posiciones de las entidades tras
 * descartar outliers estadísticos (mediana ± k·MAD por eje). Sirve para encuadrar
 * el cluster principal cuando hay entidades mal colocadas muy lejos (XREF con
 * coords basura) que inflarían el bbox real.
 *
 * Función PURA (sin estado, sin DOM, sin THREE) — extraída de LayoutBuilder para
 * aislar el motor/negocio de la presentación. La capa app (el visor) la invoca
 * para decidir el encuadre; no toca la escena.
 *
 * @param {Object} cadJson
 * @param {number} [k=30] - margen en MAD; generoso para no recortar el diseño.
 * @returns {?{minX:number, maxX:number, minY:number, maxY:number, removed:number}}
 *          null si no aplica (pocas entidades o sin puntos finitos).
 */
export function computeRobustBounds(cadJson, k = 30) {
  const ents = cadJson && cadJson.entities
  if (!ents) return null
  const list = Array.isArray(ents) ? ents : Object.values(ents)
  const xs = [], ys = []
  const push = (p) => {
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) { xs.push(p.x); ys.push(p.y) }
  }
  for (const e of list) {
    const t = e && e.type
    if (t === 'INSERT' || t === 'TEXT' || t === 'MTEXT') {
      push(e.position || e.startPoint)
    } else if (t === 'LINE' || t === 'LWPOLYLINE' || t === 'POLYLINE') {
      const vs = e.vertices || []
      for (const v of vs) push(v)
    } else if (t === 'CIRCLE' || t === 'ARC' || t === 'ELLIPSE') {
      push(e.center)
    }
  }
  if (xs.length < 20) return null
  const median = (arr) => {
    const s = arr.slice().sort((a, b) => a - b)
    const n = s.length
    return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2
  }
  const medX = median(xs), medY = median(ys)
  const madX = median(xs.map(x => Math.abs(x - medX))) || 1
  const madY = median(ys.map(y => Math.abs(y - medY))) || 1
  const loX = medX - k * madX, hiX = medX + k * madX
  const loY = medY - k * madY, hiY = medY + k * madY
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, kept = 0
  for (let i = 0; i < xs.length; i++) {
    const x = xs[i], y = ys[i]
    if (x < loX || x > hiX || y < loY || y > hiY) continue
    kept++
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  if (!kept || !Number.isFinite(minX)) return null
  return { minX, maxX, minY, maxY, removed: xs.length - kept }
}
